import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Globe, Camera } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const CreativeCrimsonTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#B71C1C';
    const { fontFamily = 'font-sans', textColor = '#1e293b', backgroundColor = '#ffffff', sectionBackgroundColor = '#ffffff', imageBorderColor = primaryColor } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+001 123 456 789' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'info@yourmail.com' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'www.yourwebsite.com' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974&auto=format&fit=crop'
        : '');

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex flex-col overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor, color: textColor }}>
            {/* Top Bar Decoration */}
            <div className="h-4" style={{ backgroundColor: primaryColor }}></div>

            <div className="flex flex-1">
                {/* Left Column */}
                <div data-cv-sidebar className="w-[35%] p-10 flex flex-col border-r-4 overflow-hidden" style={{ borderColor: primaryColor, backgroundColor: sectionBackgroundColor }}>
                    {isContinuationPage ? (
                    <div className="mt-4 mb-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest" style={{ color: textColor, opacity: 0.7 }}>
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider mt-2" style={{ color: textColor, opacity: 0.4 }}>{CV_LABELS.suite}</p>
                    </div>
                    ) : (
                    <>
                {(photoSrc || showPlaceholders) && (
                    <div className="relative mb-6">
                        <div className="w-full aspect-square rounded-full border-[10px] overflow-hidden shadow-xl" style={{ borderColor: imageBorderColor }}>
                            {photoSrc ? (
                            <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                            ) : null}
                        </div>
                        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-6 py-2 shadow-lg rounded-full border border-slate-100">
                            <Camera className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>
                    </div>
                    )}
                    </>
                    )}

                    {(data.education.length > 0 || (!isContinuationPage && showPlaceholders)) && (
                    <section className="mb-5">
                        <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="underline" />
                        <div className="cv-fs-education space-y-6">
                            {data.education.length > 0 ? data.education.map((edu, i) => (
                                <div
                                    key={edu.id || i}
                                    data-cv-block
                                    data-cv-item-id={edu.id || undefined}
                                >
                                    <p className="text-xs font-black text-slate-900 mb-1">{edu.startDate} - {frPresent(edu.isCurrent, edu.endDate)}</p>
                                    <p className="text-[10px] font-bold uppercase" style={{ color: primaryColor }}>{edu.degree}</p>
                                    <p className="text-[10px] text-slate-400 font-bold italic">{edu.institution}</p>
                                    {edu.description && (
                                        <p className="text-[10px] font-medium leading-normal mt-2" style={{ color: textColor, opacity: 0.6 }}>{edu.description}</p>
                                    )}
                                </div>
                            )) : showPlaceholders ? [2011, 2015].map(year => (
                                <div key={year}>
                                    <p className="text-xs font-black text-slate-900 mb-1">{year}</p>
                                    <p className="text-[10px] font-bold uppercase" style={{ color: primaryColor }}>Diplôme</p>
                                    <p className="text-[10px] text-slate-400 font-bold italic">Établissement</p>
                                </div>
                            )) : null}
                        </div>
                    </section>
                    )}

                    {((data.languages && data.languages.length > 0) || (!isContinuationPage && showPlaceholders)) && (
                    <section className="mb-5">
                        <SectionHeading title={CV_LABELS.languages} isContinuation={langContinued} primaryColor={primaryColor} variant="underline" />
                        <div className="cv-fs-languages space-y-3">
                            {(data.languages && data.languages.length > 0 ? data.languages : showPlaceholders ? [
                                { name: 'French', level: 100 }, { name: 'Chinese', level: 85 }, { name: 'Spanish', level: 70 },
                                { name: 'English', level: 55 }, { name: 'Hindi', level: 40 }
                            ] : []).map((lang, i) => (
                                <div
                                    key={lang.id || lang.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={lang.id || undefined}
                                >
                                    <span className="text-[10px] font-bold text-slate-600 w-[4.5rem] shrink-0 truncate" title={lang.name}>
                                        {lang.name}
                                    </span>
                                    <div className="h-1.5 flex-1 min-w-0 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full" style={{ width: `${lang.level}%`, backgroundColor: primaryColor }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    )}

                    {((data.skills && data.skills.length > 0) || (!isContinuationPage && showPlaceholders)) && (
                    <section className="min-w-0">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="underline" />
                        <div className="cv-fs-skills space-y-2.5">
                            {(data.skills && data.skills.length > 0 ? data.skills : showPlaceholders ? [
                                { name: 'Creativity', level: 90 }, { name: 'Teamwork', level: 80 }, { name: 'Management', level: 70 }
                            ] : []).map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <span className="text-[10px] font-bold text-slate-600 w-[4.5rem] shrink-0 truncate" title={skill.name}>
                                        {skill.name}
                                    </span>
                                    <div className="h-1.5 flex-1 min-w-0 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full" style={{ width: `${skill.level}%`, backgroundColor: primaryColor }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    )}
                </div>

                {/* Right Column */}
                <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-12 flex flex-col" style={{ backgroundColor: backgroundColor }}>

                    {isContinuationPage ? (
                        <div className="mb-4 pb-2 border-b border-slate-200">
                            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: textColor, opacity: 0.5 }}>{CV_LABELS.suite}</p>
                        </div>
                    ) : (
                    <header className="mb-6 flex justify-between items-start">
                        <div>
                            <h1 className="cv-fs-name text-5xl font-black tracking-tighter uppercase leading-none mb-2" style={{ color: '#' === textColor[0] ? textColor : '#0f172a' }}>
                                {data.personalInfo.firstName || (showPlaceholders ? 'TAYA' : '')} <br />
                                <span style={{ color: primaryColor }}>{data.personalInfo.lastName || (showPlaceholders ? 'MIASS' : '')}</span>
                            </h1>
                            <p className="cv-fs-profession text-lg font-bold uppercase tracking-[0.3em]" style={{ color: textColor, opacity: 0.6 }}>
                                {data.personalInfo.profession || (showPlaceholders ? 'WEB DESIGNER & DEVELOPER' : '')}
                            </p>
                        </div>
                        <div className="cv-fs-contact text-right space-y-1">
                            {phone && <p className="text-[10px] font-black" style={{ color: textColor, opacity: 0.5 }}>{phone}</p>}
                            {email && <p className="text-[10px] font-black" style={{ color: textColor, opacity: 0.5 }}>{email}</p>}
                            {website && <p className="text-[10px] font-black" style={{ color: textColor, opacity: 0.5 }}>{website}</p>}
                        </div>
                    </header>
                    )}

                    {summary && (
                    <section className="mb-6">
                        <h2 className="text-xl font-black uppercase tracking-[0.2em] mb-6 flex items-center justify-between" style={{ color: primaryColor }}>
                            {CV_LABELS.profile} <div className="h-1 flex-1 bg-slate-100 ml-6 rounded-full"></div>
                        </h2>
                        <p className="cv-fs-summary text-xs leading-relaxed font-bold italic" style={{ color: textColor, opacity: 0.7 }}>
                            {summary}
                        </p>
                    </section>
                    )}


                    {(data.experience.length > 0 || showPlaceholders) && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-experience space-y-5">
                            {data.experience.length > 0 ? data.experience.map((exp, i) => (
                                <div
                                    key={exp.id || i}
                                    className="flex gap-6"
                                    data-cv-block
                                    data-cv-item-id={exp.id || undefined}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                                        <div className="w-0.5 flex-1 my-1" style={{ backgroundColor: `${primaryColor}33` }}></div>
                                    </div>
                                    <div>
                                        <p className="text-xs font-black" style={{ color: textColor, opacity: 0.5 }}>{exp.startDate} - {frPresent(exp.isCurrent, exp.endDate)}</p>
                                        <h3 className="text-sm font-black uppercase" style={{ color: textColor }}>{exp.position}</h3>
                                        <p className="text-[10px] font-bold" style={{ color: primaryColor }}>{exp.company}</p>
                                        <p className="text-[10px] font-medium leading-normal mt-2" style={{ color: textColor, opacity: 0.6 }}>
                                            {exp.description || "Lorem ipsum simply dummy text of the printing and typesetting industry's standard dummy text ever since the 1500s."}
                                        </p>
                                    </div>
                                </div>
                            )) : showPlaceholders ? (
                                [1, 2, 3].map(i => (
                                    <div key={i} className="flex gap-6">
                                        <div className="flex flex-col items-center">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                                            <div className="w-0.5 flex-1 my-1" style={{ backgroundColor: `${primaryColor}33` }}></div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-black" style={{ color: textColor, opacity: 0.5 }}>2015 - 2017</p>
                                            <h3 className="text-sm font-black uppercase" style={{ color: textColor }}>Computer Software Developer</h3>
                                            <p className="text-[10px] font-bold" style={{ color: primaryColor }}>Entreprise</p>
                                            <p className="text-[10px] font-medium leading-normal mt-2" style={{ color: textColor, opacity: 0.6 }}>
                                                Lorem ipsum simply dummy text of the printing and typesetting industry's standard dummy text ever since the 1500s.
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : null}
                        </div>
                    </section>
                    )}


                    {(showPlaceholders || (data.references && data.references.length > 0)) && (
                    <section>
                        <h2 className="text-xl font-black uppercase tracking-[0.2em] mb-6 flex items-center justify-between" style={{ color: primaryColor }}>
                            {CV_LABELS.references} <div className="h-1 flex-1 bg-slate-100 ml-6 rounded-full"></div>
                        </h2>
                        <div className="cv-fs-references grid grid-cols-2 gap-8">
                            {(data.references && data.references.length > 0 ? data.references.slice(0, 2) : showPlaceholders ? [
                                { name: 'Nom de la référence', position: 'Poste', company: 'Entreprise', phone: '', email: 'info@yourmail.com' },
                                { name: 'Nom de la référence', position: 'Poste', company: 'Entreprise', phone: '', email: 'info@yourmail.com' },
                            ] : []).map((ref, i) => (
                                <div
                                    key={ref.id || i}
                                    data-cv-block
                                    data-cv-item-id={ref.id || undefined}
                                >
                                    <p className="text-sm font-black text-slate-900">{ref.name}</p>
                                    <p className="text-[10px] font-bold text-slate-400">{ref.position}</p>
                                    <p className="text-[10px] font-bold" style={{ color: primaryColor }}>{ref.company}</p>
                                    <p className="text-[10px] font-medium text-slate-400 mt-1 italic">Mail: {ref.email}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                    )}
                </div>
            </div>

            {/* Bottom Accent */}
            <div className="h-1 w-[65%] self-end" style={{ backgroundColor: primaryColor }}></div>
        </div>
    );
};

export default CreativeCrimsonTemplate;
